import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Virtuoso } from 'react-virtuoso';
import { Strings } from '../../constants';
import { useChannelList } from '../../hooks/useChannelList'
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

import {
  CobuStack,
  CobuText,
  CobuImage,
  CobuTextAvatar,
  CobuAlert,
  CobuHeader,
} from '@cobu/ui';
import { URLS } from '../../constants';
import { useTheme, Sheet } from 'tamagui';
import Bubble from '../../assets/icons/Bubble';
import { logger } from '../../utils/logger';
interface RSVP {
  uuid?: string;
  totalAttendees: number;
  isExternal?: boolean;
  user: {
    uuid?: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    profileImage?: string;
    role?: string;
  };
}

interface AllMembersModalProps {
  visible: boolean;
  onClose: () => void;
  rsvps: RSVP[];
  eventTitle: string;
  totalAttendance: number;
  currentUserId?: string;
  enableDM?: boolean;
  dmFeatureFlag?: boolean;
}

interface MemberItemProps {
  rsvp: RSVP;
  onUserPress: (userId: string) => void;
  currentUserId?: string;
  onDM?: (userId: string, displayName: string) => void; 
  canShowDM: boolean;
}

function resolveDisplayName(r: RSVP): string {
  return (
    r.user.displayName ??
    (`${r.user.firstName ?? ''} ${r.user.lastName ?? ''}`.trim() || r.user.email) ??
    ''
  );
}

function MemberItem({ rsvp, onUserPress, currentUserId, onDM, canShowDM }: MemberItemProps) {
  const { user, totalAttendees } = rsvp;
  const strings = Strings.get();
  const theme = useTheme();

  const displayName = resolveDisplayName(rsvp);

  return (
    <CobuStack onPress={user.uuid ? () => onUserPress(user.uuid!) : undefined}>
      <CobuStack layout="horizontal" ai="center" p="$4" gap="$3">
        {/* Profile Image or Text Avatar */}
        {user.profileImage && user.profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
          <CobuImage
            src={user.profileImage}
            width='$10'
            height='$10'
            borderRadius='$full'
          />
        ) : (
          <CobuTextAvatar name={displayName} uuid={user.uuid ?? ''} />
        )}

        {/* Member Info */}
        <CobuStack f={1} gap="$1">
          <CobuText variant="body" fontSize='$4' fontWeight="bold" numberOfLines={1} color="$cobuDarkBlue">
            {displayName}
          </CobuText>
          {rsvp.isExternal && (
            <CobuText variant="body" fontSize='$2' color="$colorMuted">
              {strings.ui.allMembers.externalLabel}
            </CobuText>
          )}
          {totalAttendees > 1 && (
            <CobuText variant="body" fontSize='$2' fontWeight='bold' color="$colorMuted">
              + {totalAttendees - 1} {totalAttendees > 2 ? strings.ui.members.guests : strings.ui.members.guest}
            </CobuText>
          )}
        </CobuStack>

        {/* DM Button */}
        {canShowDM && user.uuid && currentUserId !== user.uuid && onDM ? (
          <CobuStack
            p="$2"
            onPress={(e: any) => {
              e?.stopPropagation?.();
              onDM(user.uuid!, displayName)
            }}
          >
            <Bubble size={24} color={theme.colorMuted?.val || '#9B9B9B'} />
          </CobuStack>
        ) : null}

      </CobuStack>
    </CobuStack>
  );
}

export default function AllMembersModal({
  visible,
  onClose,
  rsvps,
  eventTitle,
  totalAttendance,
  currentUserId,
  dmFeatureFlag = false,
}: AllMembersModalProps) {
  const strings = Strings.get();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertDescription, setAlertDescription] = useState('');
  const { showDM } = useFeatureFlags();

  const { navigateToChannel, isStreamAvailable, isLoading } = useChannelList({
    enabled: false,
  });

  const defaultDMPress = async (userId: string, displayName: string) => {
    try {
      if (!isStreamAvailable) {
        // Alert.alert(strings.ui.allMembers.directMessages.unavailable, strings.ui.allMembers.directMessages.notAvailable);
        // window.alert(`${strings.ui.allMembers.directMessages.unavailable}: ${strings.ui.allMembers.directMessages.notAvailable}`);
        setAlertTitle(strings.ui.allMembers.directMessages.unavailable);
        setAlertDescription(strings.ui.allMembers.directMessages.notAvailable);
        setAlertOpen(true);
        return;
      }
      const channel = await navigateToChannel(userId, displayName);
      const target = channel?.cid || channel?.id
      if (target) {
        navigate(`/chat/${target}`)
      } else {
        setAlertTitle(strings.ui.allMembers.directMessages.unableToOpen);
        setAlertDescription(strings.ui.allMembers.directMessages.tryAgain);
        setAlertOpen(true);
      }
    } catch (err: any) {
      logger.error('onDMPress error:', err);
      // window.alert(`${strings.ui.allMembers.directMessages.unableToOpen}: ${err?.message || strings.ui.allMembers.directMessages.tryAgain}`);
      setAlertTitle(strings.ui.allMembers.directMessages.unableToOpen);
      setAlertDescription(err?.message || strings.ui.allMembers.directMessages.tryAgain);
      setAlertOpen(true);
    }
  };

  // Filter and sort members based on search term + exclude blocked/deactivated
  const filteredMembers = useMemo(() => {
    return rsvps
      .filter((r) =>
        resolveDisplayName(r).toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) =>
        resolveDisplayName(a).localeCompare(resolveDisplayName(b), undefined, { sensitivity: 'base' })
      );
  }, [rsvps, searchTerm]);

  // const headerCount = filteredMembers.length;

  const canShowDMFor = (r: RSVP) => {
    const roleName = r.user.role;
    return showDM && dmFeatureFlag && roleName === 'RESIDENT';
  };

  const handleDM = async (uuid: string, displayName: string) => {
    setSearchTerm('');
    onClose();
    if (!isLoading) {
      await defaultDMPress(uuid, displayName);
    }
  };  
    


  const handleUserPress = (userId: string) => {
    onClose();
    navigate(`/public-profile/${userId}`);
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const renderHeader = () => (
    <CobuStack>
      {/* Header */}
      <CobuHeader
        renderLeft={() => (
          <CobuStack onPress={handleClose}>
            <CobuStack w="$10" ai="flex-start">
              <CloseIcon style={{ width: 24, height: 24, color: theme.colorMuted?.val }} />
            </CobuStack>
          </CobuStack>
        )}
        renderRight={() => <CobuStack w="$10" />}
        titleComponent={() => (
          <CobuStack ai="center" gap="$1">
            <CobuText
              variant="body"
              numberOfLines={1}
              ta="center"
            >
              {eventTitle.length > 24 ? `${eventTitle.substring(0, 24)}...` : eventTitle}
            </CobuText>
            <CobuText variant="body" color="$colorMuted" ta="center">
              {totalAttendance} Member{totalAttendance > 1 ? 's' : ''}
            </CobuText>
          </CobuStack>
        )}
      />
      {/* Search */}
      <CobuStack layout="horizontal" ai="center" p="$4" gap="$3">
        <SearchIcon style={{ width: 20, height: 20, color: theme.colorMuted?.val }} />
        <input
          style={{
            flex: 1,
            fontSize: 16,
            color: '#333',
            padding: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm((e.target as HTMLInputElement).value)}
          placeholder={strings.ui.modal.searchPeople}
        />
      </CobuStack>

      {/* Members Label */}
      <CobuStack px="$4" pt="$4" bg="$background">
        <CobuText
          variant="body"
          fontSize='$2'
          fontWeight="bold"
          color="$colorMuted"
          style={{ letterSpacing: 0.5 }}
        >
                              {strings.ui.allMembers.membersLabel}
        </CobuText>
      </CobuStack>
    </CobuStack>
  );

  const renderMemberItem = ({ item }: { item: RSVP }) => (
    <MemberItem
      rsvp={item}
      onUserPress={handleUserPress}
      currentUserId={currentUserId}
      onDM={handleDM}
      canShowDM={canShowDMFor(item)}
    />
  );

  const renderEmptyState = () => (
    <CobuStack f={1} ai="center" jc="center" p="$4">
      <CobuText variant="body" color="$colorMuted" ta="center">
        {searchTerm
          ? strings.ui.allMembers.noMembersSearch
          : strings.ui.allMembers.noRsvp}
      </CobuText>
    </CobuStack>
  );

  return (
    <>
      <Sheet 
        modal 
        open={visible} 
        onOpenChange={(open: boolean) => { if (!open) handleClose(); }}
        snapPoints={[70,40,40]}
        snapPointsMode="percent"
        dismissOnSnapToBottom
      >
        <Sheet.Overlay
          animation={undefined as any}
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
          bg={theme.blackTransparent50?.val}
        />
        <Sheet.Frame br="$3" style={{ width: '100%', padding: 0, backgroundColor: '#fff' }}>
          <CobuStack f={1} bg="white" gap="$0" style={{ maxHeight: '80vh' }}>
            {renderHeader()}
            <CobuStack f={1} style={{ maxHeight: '80vh' }}>
              {filteredMembers.length === 0 ? (
                renderEmptyState()
              ) : (
                <>
                  <Virtuoso
                    data={filteredMembers}
                    computeItemKey={(index, item) => (item?.uuid ?? item?.user?.uuid ?? String(index))}
                    itemContent={(index, item) => (
                      <CobuStack px={0}>
                        {renderMemberItem({ item })}
                      </CobuStack>
                    )}
                    overscan={400}
                    style={{ flex: 1, width: '100%', height: '60vh' }}
                  />
                </>
              )}
            </CobuStack>
          </CobuStack>
        </Sheet.Frame>
      </Sheet>

      {/* CobuAlert replacing window.alert */}
      <CobuAlert
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertTitle}
        description={alertDescription}
        acceptText={strings.common.buttonLabels.ok}
      />
    </>
  );
}
